import React, { useCallback, useEffect, useMemo, useState } from 'react';
import KpiChartPanel from '../kpi-dashboard/charts/KpiChartPanel';
import BandwidthHeatmapValueUnitMenu from './BandwidthHeatmapValueUnitMenu';
import BandwidthPanelExportShell from './BandwidthPanelExportShell';
import { exportBandwidthHeatmapMatrixExcel } from './bandwidthDashboardExcelExport';
import { exportBandwidthHeatmapMatrixPdf } from './bandwidthDashboardPdfExport';
import {
  convertHeatmapCellsToPercent,
  formatHeatmapCellValue,
  type HeatmapValueUnit,
} from './bandwidthHeatmapMatrix';
import {
  bwKpiPanelSubtitleClass,
  bwKpiTableHeaderCellClass,
  bwKpiTableHeaderClass,
  bwKpiTableHeaderFirstColClass,
  bwKpiTableBodyFirstColClassWithBg,
  bwKpiTableSelectedRowClass,
  bwKpiTableZebraEven,
  bwKpiTableZebraOdd,
} from './bandwidthKpiTableChrome';

type SortDir = 'asc' | 'desc';
type HeatmapSort = { kind: 'label'; dir: SortDir } | { kind: 'column'; colIndex: number; dir: SortDir };

function sortIndicator(active: boolean, dir: SortDir): string {
  if (!active) return '↕';
  return dir === 'desc' ? '↓' : '↑';
}

function ariaSortForLabelColumn(sort: HeatmapSort): 'ascending' | 'descending' | 'none' {
  if (sort.kind !== 'label') return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
}

function ariaSortForDataColumn(sort: HeatmapSort, colIndex: number): 'ascending' | 'descending' | 'none' {
  if (sort.kind !== 'column' || sort.colIndex !== colIndex) return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
}

export interface BandwidthHeatmapTableProps {
  title: string;
  /** Shown under the title strip (e.g. active drill-down scope). */
  subtitle?: string;
  rowLabelHeader: string;
  rowLabels: string[];
  columnLabels: string[];
  /** Same dimensions as rowLabels × columnLabels; minutes or hours per cell */
  cells: number[][];
  valueSuffix?: string;
  emptyMessage?: string;
  /** When set, non-zero cells are clickable for drill-down; same cell again clears (handled by parent). */
  interactive?: boolean;
  selectedRowLabel?: string | null;
  /** When set without `selectedColLabel`, the whole row (non-zero cells) is treated as selected (e.g. type-row drill). */
  selectedColLabel?: string | null;
  onCellClick?: (rowLabel: string, colLabel: string) => void;
  /** Click the row label (type) when that row has any non-zero time; parent toggles type-wide drill. */
  onRowLabelClick?: (rowLabel: string) => void;
  /** Alt+click the column header (after sort) to drill by that column (e.g. customer or team name). */
  onColumnLabelClick?: (colLabel: string) => void;
  /** When set, shows Export → PDF / Excel for the matrix as currently sorted. */
  exportMatrix?: { filePrefix: string; sheetName: string; dateFrom: string; dateTo: string };
  /** When true, shows a Minutes / Percent dropdown (percent = share of each column total). */
  valueUnitToggle?: boolean;
}

const BandwidthHeatmapTable: React.FC<BandwidthHeatmapTableProps> = ({
  title,
  subtitle,
  rowLabelHeader,
  rowLabels,
  columnLabels,
  cells,
  valueSuffix = ' min',
  emptyMessage = 'No data for the selected filters.',
  interactive = false,
  selectedRowLabel = null,
  selectedColLabel = null,
  onCellClick,
  onRowLabelClick,
  onColumnLabelClick,
  exportMatrix,
  valueUnitToggle = false,
}) => {
  const [sort, setSort] = useState<HeatmapSort>({ kind: 'label', dir: 'asc' });
  const [valueUnit, setValueUnit] = useState<HeatmapValueUnit>('min');

  const matrixLabelsKey = `${rowLabels.join('\0')}\n${columnLabels.join('\0')}`;
  useEffect(() => {
    setSort({ kind: 'label', dir: 'asc' });
  }, [matrixLabelsKey]);

  const hasRows = rowLabels.length > 0 && columnLabels.length > 0;
  const activeValueUnit = valueUnitToggle ? valueUnit : 'min';

  const unitCells = useMemo(
    () => (activeValueUnit === 'percent' ? convertHeatmapCellsToPercent(cells) : cells),
    [cells, activeValueUnit]
  );

  const { displayRowLabels, displayCells, displayRawCells } = useMemo(() => {
    const n = rowLabels.length;
    if (n === 0 || columnLabels.length === 0) {
      return { displayRowLabels: rowLabels, displayCells: unitCells, displayRawCells: cells };
    }
    const idx = [...Array(n).keys()];
    if (sort.kind === 'label') {
      idx.sort((a, b) => {
        const cmp = rowLabels[a].localeCompare(rowLabels[b], undefined, { sensitivity: 'base' });
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    } else {
      const ci = sort.colIndex;
      idx.sort((a, b) => {
        const va = unitCells[a]?.[ci] ?? 0;
        const vb = unitCells[b]?.[ci] ?? 0;
        if (va !== vb) return sort.dir === 'desc' ? vb - va : va - vb;
        return rowLabels[a].localeCompare(rowLabels[b], undefined, { sensitivity: 'base' });
      });
    }
    return {
      displayRowLabels: idx.map((i) => rowLabels[i]),
      displayCells: idx.map((i) => unitCells[i] ?? []),
      displayRawCells: idx.map((i) => cells[i] ?? []),
    };
  }, [rowLabels, columnLabels.length, cells, unitCells, sort]);

  const onRowHeaderSortClick = useCallback(() => {
    setSort((prev) => {
      if (prev.kind === 'label') return { kind: 'label', dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return { kind: 'label', dir: 'asc' };
    });
  }, []);

  const onColumnHeaderSortClick = useCallback((colIndex: number) => {
    setSort((prev) => {
      if (prev.kind === 'column' && prev.colIndex === colIndex) {
        return { kind: 'column', colIndex, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
      }
      return { kind: 'column', colIndex, dir: 'desc' };
    });
  }, []);

  const sortHeadersDisabled = !hasRows;

  const exportUnitNote =
    activeValueUnit === 'percent'
      ? 'Cell values are percent of each column total.'
      : valueSuffix.includes('min')
        ? 'Cell values are minutes.'
        : undefined;

  const titleEnd =
    (valueUnitToggle || (exportMatrix && hasRows)) ? (
      <div className="flex items-center gap-2">
        {valueUnitToggle ? (
          <BandwidthHeatmapValueUnitMenu
            value={activeValueUnit}
            onChange={setValueUnit}
            disabled={!hasRows}
          />
        ) : null}
        {exportMatrix && hasRows ? (
          <BandwidthPanelExportShell
            disabled={false}
            ariaLabel={`Export ${title}`}
            buildPdf={() =>
              exportBandwidthHeatmapMatrixPdf({
                documentTitle: `Bandwidth dashboard — ${title}`,
                filePrefix: exportMatrix.filePrefix,
                rowLabelHeader,
                rowLabels: displayRowLabels,
                columnLabels,
                cells: displayCells,
                dateFrom: exportMatrix.dateFrom,
                dateTo: exportMatrix.dateTo,
                unitNote: exportUnitNote,
                cellValueUnit: activeValueUnit,
              })
            }
            buildExcel={() =>
              exportBandwidthHeatmapMatrixExcel({
                filePrefix: exportMatrix.filePrefix,
                sheetName: exportMatrix.sheetName,
                rowLabelHeader,
                rowLabels: displayRowLabels,
                columnLabels,
                cells: displayCells,
                dateFrom: exportMatrix.dateFrom,
                dateTo: exportMatrix.dateTo,
                cellValueUnit: activeValueUnit,
              })
            }
          />
        ) : null}
      </div>
    ) : undefined;

  return (
    <KpiChartPanel
      title={title}
      titleClassName="!pl-0"
      titleEnd={titleEnd}
      className="h-full min-h-0 max-h-[400px]"
      flexFill
      bodyClassName="px-0 pb-0 pt-0"
    >
      {subtitle ? (
        <p className={bwKpiPanelSubtitleClass}>{subtitle}</p>
      ) : null}
      <div className="min-h-0 flex-1 overflow-auto">
        {!hasRows ? (
          <p className="px-3 py-8 text-center text-sm text-slate-500">{emptyMessage}</p>
        ) : (
          <table className="w-full min-w-[120px] border-collapse text-left text-xs">
            <thead>
              <tr className={bwKpiTableHeaderClass}>
                <th
                  scope="col"
                  aria-sort={ariaSortForLabelColumn(sort)}
                  className={`min-w-[120px] whitespace-nowrap px-2 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass} ${bwKpiTableHeaderFirstColClass}`}
                >
                  <button
                    type="button"
                    disabled={sortHeadersDisabled}
                    onClick={onRowHeaderSortClick}
                    className="inline-flex max-w-full items-center gap-1 rounded px-0.5 text-left font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                  >
                    <span className="truncate">{rowLabelHeader}</span>
                    <span className="shrink-0 font-normal opacity-90" aria-hidden>
                      {sortIndicator(sort.kind === 'label', sort.kind === 'label' ? sort.dir : 'asc')}
                    </span>
                  </button>
                </th>
                {columnLabels.map((c, ci) => (
                  <th
                    key={`${ci}-${c}`}
                    scope="col"
                    aria-sort={ariaSortForDataColumn(sort, ci)}
                    className={`min-w-[120px] max-w-[140px] px-2 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide leading-tight ${bwKpiTableHeaderCellClass}`}
                  >
                    <button
                      type="button"
                      disabled={sortHeadersDisabled}
                      onClick={(e) => {
                        if (interactive && onColumnLabelClick && e.altKey) {
                          e.preventDefault();
                          onColumnLabelClick(c);
                          return;
                        }
                        onColumnHeaderSortClick(ci);
                      }}
                      title={
                        onColumnLabelClick && interactive
                          ? `${c} — click to sort, Alt+click to filter by this column`
                          : c
                      }
                      className="inline-flex max-w-full items-start gap-1 rounded px-0.5 text-left font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                    >
                      <span className="line-clamp-3 min-w-0 flex-1 text-left">{c}</span>
                      <span className="shrink-0 font-normal opacity-90" aria-hidden>
                        {sortIndicator(sort.kind === 'column' && sort.colIndex === ci, sort.kind === 'column' && sort.colIndex === ci ? sort.dir : 'asc')}
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-slate-800">
              {displayRowLabels.map((row, ri) => {
                const stripe = ri % 2 === 0 ? bwKpiTableZebraOdd : bwKpiTableZebraEven;
                const rowLabel = row;
                const rowTotal = columnLabels.reduce((s, _, ci) => s + (displayRawCells[ri]?.[ci] ?? 0), 0);
                const rowLabelInteractive = Boolean(interactive && onRowLabelClick && rowTotal > 0);
                const rowOnlySelection =
                  selectedRowLabel === rowLabel && selectedColLabel == null && selectedRowLabel != null;
                return (
                  <tr key={`${ri}-${row}`} className={`border-b border-slate-100 ${stripe}`}>
                    <td className={`min-w-[120px] whitespace-nowrap px-2 py-1.5 font-medium text-slate-700 ${bwKpiTableBodyFirstColClassWithBg({ rowIndex: ri, selected: rowOnlySelection })}`}>
                      {rowLabelInteractive ? (
                        <button
                          type="button"
                          title={`${row} — click to filter by this task type`}
                          onClick={() => onRowLabelClick!(rowLabel)}
                          className={`max-w-full truncate rounded px-0.5 text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${
                            rowOnlySelection ? 'font-semibold text-indigo-900' : ''
                          }`}
                        >
                          {row}
                        </button>
                      ) : (
                        <span title={row}>{row}</span>
                      )}
                    </td>
                    {columnLabels.map((colLabel, ci) => {
                      const rawV = displayRawCells[ri]?.[ci] ?? 0;
                      const displayV = displayCells[ri]?.[ci] ?? 0;
                      const isSelected =
                        rowOnlySelection && rawV > 0
                          ? true
                          : selectedRowLabel === rowLabel && selectedColLabel === colLabel && rawV > 0;
                      const clickable = interactive && rawV > 0 && onCellClick;
                      const cellTitle =
                        rawV > 0
                          ? `${rowLabel} · ${colLabel}: ${formatHeatmapCellValue(displayV, activeValueUnit)}`
                          : undefined;
                      return (
                        <td
                          key={`${ri}-${ci}`}
                          className={`min-w-[120px] px-1 py-1.5 text-center tabular-nums transition-colors ${
                            isSelected ? `${bwKpiTableSelectedRowClass} font-semibold text-slate-900` : stripe
                          } ${
                            clickable
                              ? isSelected
                                ? 'cursor-pointer select-none hover:bg-indigo-100/75'
                                : 'cursor-pointer select-none hover:bg-indigo-50/80'
                              : ''
                          }`}
                          title={cellTitle}
                          onClick={
                            clickable
                              ? () => {
                                  onCellClick(rowLabel, colLabel);
                                }
                              : undefined
                          }
                          onKeyDown={
                            clickable
                              ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onCellClick(rowLabel, colLabel);
                                  }
                                }
                              : undefined
                          }
                          role={clickable ? 'button' : undefined}
                          tabIndex={clickable ? 0 : undefined}
                        >
                          {rawV > 0 ? (
                            formatHeatmapCellValue(displayV, activeValueUnit)
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </KpiChartPanel>
  );
};

export default BandwidthHeatmapTable;
